import React from 'react';
import styled from 'styled-components';
import {
  Button,
  Text,
  TextButton,
  IconCheckmark,
  IconWarning,
} from '@zillow/constellation';
import Spinner from './Spinner';

export enum FileWorkflowStepType {
  Add,
  Pending,
  Processing,
  Failed,
  Complete,
}

export type FileWorkflowActon = {
  action: () => void;
  description: string;
};

type ContainerProps = {
  hasPrimaryAction: boolean;
  hasSecondaryAction: boolean;
};

const BaseContainer = styled.div<ContainerProps>`
  display: grid;
  column-gap: 1rem;
  align-items: center;
  min-height: 2.75rem;
`;

const PendingContainer = styled(BaseContainer)`
  grid-template-areas: '. primary-action';
  grid-template-columns: 1fr auto;

  @media (max-width: 500px) {
    grid-template-areas: 'primary-action';
    grid-template-columns: 1fr;
  }
`;

const ProcessingContainer = styled(BaseContainer)`
  grid-template-areas: 'description';
  grid-template-columns: 1fr;
  justify-items: start;

  @media (max-width: 500px) {
    justify-items: center;
  }
`;

const FailedContainer = styled(BaseContainer)`
  grid-template-areas: ${props =>
    `'description ${
      props.hasSecondaryAction ? 'secondary-action' : ''
    } primary-action'`};
  grid-template-columns: ${props =>
    `1fr ${props.hasSecondaryAction ? 'auto' : ''} auto`};

  @media (max-width: 500px) {
    grid-template-areas: ${props =>
      `'${
        props.hasSecondaryAction ? '. secondary-action' : ''
      } primary-action'`};
    grid-template-columns: ${props =>
      `1fr ${props.hasSecondaryAction ? 'auto auto' : ''}`};
    --_description-display: none;
  }
`;

const CompleteContainer = styled(BaseContainer)`
  grid-template-areas: ${props =>
    `'description ${props.hasPrimaryAction ? 'primary-action' : ''}'`};
  grid-template-columns: ${props =>
    `1fr ${props.hasPrimaryAction ? 'auto' : ''}`};

  @media (max-width: 500px) {
    grid-template-areas: ${props =>
      props.hasPrimaryAction ? `'primary-action'` : `'description'`};
    grid-template-columns: 1fr;
    justify-items: center;
    --_description-display: ${props => (props.hasPrimaryAction ? 'none' : '')};
  }
`;

const Description = styled.div`
  display: var(--_description-display, flex);
  grid-area: description;
`;

const Indicator = styled.div`
  display: inline-block;
  margin-right: 0.25rem;
  align-self: center;
`;

const SecondaryAction = styled(TextButton)`
  grid-area: secondary-action;
`;

const PrimaryAction = styled(Button)`
  grid-area: primary-action;
`;

function getContainer(stepType: FileWorkflowStepType) {
  switch (stepType) {
    case FileWorkflowStepType.Pending:
      return PendingContainer;
    case FileWorkflowStepType.Processing:
      return ProcessingContainer;
    case FileWorkflowStepType.Failed:
      return FailedContainer;
    case FileWorkflowStepType.Complete:
      return CompleteContainer;
  }

  return undefined;
}

export type FileWorkflowStepConsumableProps = {
  stepType: FileWorkflowStepType;
  description?: string;
  primaryAction?: FileWorkflowActon;
  secondaryAction?: FileWorkflowActon;
};

export type Props = {
  className?: string;
} & FileWorkflowStepConsumableProps;

export default function FileWorkflowStep({
  stepType,
  description,
  primaryAction,
  secondaryAction,
  className,
}: Props) {
  const hasPrimaryAction = !!primaryAction;
  const hasSecondaryAction = !!secondaryAction;
  const Container = getContainer(stepType);

  return Container ? (
    <Container
      hasPrimaryAction={hasPrimaryAction}
      hasSecondaryAction={hasSecondaryAction}
      className={className}
    >
      {description && (
        <Description>
          <Indicator>
            {stepType === FileWorkflowStepType.Processing ? (
              <Spinner small />
            ) : stepType === FileWorkflowStepType.Failed ? (
              <IconWarning size="sm" fontColor="orange500" />
            ) : stepType === FileWorkflowStepType.Complete ? (
              <IconCheckmark size="sm" fontColor="green400" />
            ) : null}
          </Indicator>
          <Text fontType="bodySmall">{description}</Text>
        </Description>
      )}
      {secondaryAction && (
        <SecondaryAction onClick={secondaryAction.action}>
          {secondaryAction.description}
        </SecondaryAction>
      )}
      {primaryAction && (
        <PrimaryAction
          fluid
          buttonType="primary"
          onClick={primaryAction.action}
          type="button"
        >
          {primaryAction.description}
        </PrimaryAction>
      )}
    </Container>
  ) : null;
}
